//  Payout start of day: when a merchant's day begins for Stripe's automatic payouts.
//
//  The timezone names are Stripe's, not the usual America/* ones. A US account rejects
//  America/Chicago and lists exactly these (probed in test mode, October 2026). Etc/UTC is
//  the default every account starts on, and is here so an admin can put a merchant back.
export const START_OF_DAY_TIMEZONES = [
  { value: "US/Eastern", label: "Eastern" },
  { value: "US/Central", label: "Central" },
  { value: "US/Mountain", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "US/Pacific", label: "Pacific" },
  { value: "US/Alaska", label: "Alaska" },
  { value: "US/Hawaii", label: "Hawaii" },
  { value: "Etc/UTC", label: "UTC (default)" },
];

export const DEFAULT_TIMEZONE = "Etc/UTC";

//  Stripe's allowed start-of-day windows for US accounts (private preview table). Every
//  US timezone takes 17:00 to 23:00 the evening before, then 00:00 up to a morning cutoff
//  that is earlier for Alaska and Hawaii. Minutes are 0 or 30. Etc/UTC is midnight only.
const MORNING_CUTOFF_HOUR = {
  "US/Alaska": 6,
  "US/Hawaii": 5,
};
const DEFAULT_MORNING_CUTOFF_HOUR = 7;
const EVENING_START_HOUR = 17;

function halfHours(fromHour, toHour) {
  const times = [];
  for (let hour = fromHour; hour <= toHour; hour++) {
    times.push({ hour, minutes: 0 });
    if (hour < toHour) times.push({ hour, minutes: 30 });
  }
  return times;
}

/**
 * The times Stripe allows for a timezone, in the order the day runs: 17:00 through 23:30,
 * then 00:00 through the morning cutoff.
 */
export function allowedTimesFor(timezone) {
  if (timezone === DEFAULT_TIMEZONE) return [{ hour: 0, minutes: 0 }];

  const cutoff = MORNING_CUTOFF_HOUR[timezone] ?? DEFAULT_MORNING_CUTOFF_HOUR;
  return [
    ...halfHours(EVENING_START_HOUR, 23),
    { hour: 23, minutes: 30 },
    ...halfHours(0, cutoff),
  ];
}

export function isAllowedTime(timezone, hour, minutes) {
  return allowedTimesFor(timezone).some(
    (t) => t.hour === hour && t.minutes === minutes,
  );
}

export function formatTime(hour, minutes) {
  return `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function timezoneLabel(timezone) {
  return (
    START_OF_DAY_TIMEZONES.find((tz) => tz.value === timezone)?.label ||
    timezone
  );
}

export function isDefaultStartOfDay(startOfDay) {
  return (
    !startOfDay?.timezone ||
    (startOfDay.timezone === DEFAULT_TIMEZONE &&
      !startOfDay.hour &&
      !startOfDay.minutes)
  );
}

/**
 * "00:00 Central (US/Central)", or "Default (00:00 UTC)" for an account nobody has set.
 */
export function formatStartOfDay(startOfDay) {
  if (isDefaultStartOfDay(startOfDay)) return "Default (00:00 UTC)";

  const time = formatTime(startOfDay.hour ?? 0, startOfDay.minutes ?? 0);
  const label = timezoneLabel(startOfDay.timezone);
  return label === startOfDay.timezone
    ? `${time} ${startOfDay.timezone}`
    : `${time} ${label} (${startOfDay.timezone})`;
}

/**
 * The time choices for the editor. A saved value outside Stripe's current window (set
 * before the window changed, or through Stripe directly) is kept so it can be seen and
 * left alone.
 */
export function timeOptionsFor(timezone, current) {
  const options = allowedTimesFor(timezone);
  if (
    current &&
    current.timezone === timezone &&
    !isAllowedTime(timezone, current.hour, current.minutes)
  ) {
    options.push({ hour: current.hour, minutes: current.minutes });
  }
  return options;
}
